import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import ErrorBoundary from './Control/ErrorBoundary';
import './Backbone/assets/scss/style.scss';
import './index.css';
import './App.css';
import './Control/Dropzone/styles.css';
import store from './Redux/Store';
import { queryClient } from './Core/queryClient';
import BaseApi from './Core/BaseApi';

// --- Generic Cleanup: Purge any lingering Service Workers & CacheStorage ---
// Prevents old cached chunks/HTML from freezing client browsers after CI/CD deployments.
if (typeof window !== 'undefined') {
    const originalConsoleError = console.error.bind(console);
    console.error = (...args) => {
        const error = args.find(value => value instanceof Error);
        if (error) {
            BaseApi.ReportClientException(error);
        }
        originalConsoleError(...args);
    };

    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
            for (const registration of registrations) {
                registration.unregister().catch(() => {});
            }
        }).catch(() => {});
    }
    if ('caches' in window) {
        caches.keys().then(names => {
            for (const name of names) {
                caches.delete(name).catch(() => {});
            }
        }).catch(() => {});
    }

    // --- Generic Auto-Recovery: Window-level ChunkLoadError Handler ---
    const handleChunkError = (message) => {
        if (!message) return;
        const msg = String(message).toLowerCase();
        if (
            msg.includes('chunkloaderror') ||
            msg.includes('loading chunk') ||
            msg.includes('failed to fetch dynamically imported module') ||
            msg.includes('unexpected token <') ||
            msg.includes("unexpected token '<'")
        ) {
            const lastReload = sessionStorage.getItem('chunk_reload_ts');
            const now = Date.now();
            if (!lastReload || now - parseInt(lastReload, 10) > 15000) {
                sessionStorage.setItem('chunk_reload_ts', now.toString());
                console.warn('[Global] Outdated chunk detected. Reloading to fetch the latest application build...');
                window.location.reload();
            }
        }
    };

    window.addEventListener('error', (event) => {
        if (event.error || event.message) {
            if (event.error instanceof Error) {
                BaseApi.ReportClientException(event.error);
            } else {
                void BaseApi.ReportClientError({
                    ErrorKind: 'WindowError',
                    Message: event.message || 'Unhandled browser error',
                });
            }
        }
        handleChunkError(event.message || event.error?.message);
    });

    window.addEventListener('unhandledrejection', (event) => {
        const reason = event.reason;
        if (reason instanceof Error) {
            BaseApi.ReportClientException(reason);
        } else {
            void BaseApi.ReportClientError({
                ErrorKind: 'UnhandledRejection',
                Message: String(reason || 'Unhandled promise rejection'),
            });
        }
        handleChunkError(event.reason?.message || event.reason);
    });

    // --- Global Wheel Handler: Prevent mouse wheel from accidentally changing number input values ---
    window.addEventListener(
        'wheel',
        (event) => {
            const active = document.activeElement;
            if (active && active.tagName === 'INPUT' && active.type === 'number') {
                active.blur();
            }
            const target = event.target;
            if (target && target.tagName === 'INPUT' && target.type === 'number') {
                target.blur();
            }
        },
        { passive: true, capture: true }
    );

    if (
        window.location.pathname === '/' ||
        window.location.pathname.includes('%5Cadmin') ||
        window.location.pathname.includes('\\admin')
    ) {
        window.location.replace('/admin/');
    }
}

ReactDOM.render(
    <ErrorBoundary>
        <Provider store={store}>
            <QueryClientProvider client={queryClient}>
                <BrowserRouter basename="/admin">
                    <App />
                </BrowserRouter>
            </QueryClientProvider>
        </Provider>
    </ErrorBoundary>,
    document.getElementById('root')
);